import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Divider,
  Alert,
  Button
} from '@mui/material';
import { PropertiesPanelProps } from '../../../types/PluginInterfaces';
import { VirtualRoomPainterPlugin } from '../VirtualRoomPainterPlugin';
import { VirtualRoom } from '../services/VirtualRoomStorage';
import { useInputLocking } from '../../../../../../hooks/useInputLocking';
import { InputLockReason } from '../../../../../../services/InputController';
import { ColorPicker } from './ColorPicker';
import { KindPicker } from './KindPicker';
import { SpawnPointSection } from './SpawnPointSection';

interface VirtualRoomPropertiesPanelProps extends PropertiesPanelProps {
  plugin: VirtualRoomPainterPlugin;
}

export const VirtualRoomPropertiesPanel: React.FC<VirtualRoomPropertiesPanelProps> = ({
  plugin,
  context
}) => {
  const [currentRoom, setCurrentRoom] = useState<VirtualRoom | null>(null);
  const [roomName, setRoomName] = useState('');
  const [roomColor, setRoomColor] = useState('#ff0000');
  const [uniqueRoomId, setUniqueRoomId] = useState('');
  const [proximityDistance, setProximityDistance] = useState(5);
  const [kind, setKind] = useState<VirtualRoom['kind']>('meeting');
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Input locking for text fields
  const {
    inputFocused,
    handleInputFocus,
    handleInputBlur,
    handleKeyDown,
    handleKeyUp,
    KeyboardCaptureLayer
  } = useInputLocking({
    lockReason: InputLockReason.MAP_EDITOR,
    debugLabel: 'VirtualRoomPropertiesPanel',
    enableDebugLogs: false
  });

  // Update properties when room changes
  useEffect(() => {
    const room = plugin.getCurrentRoom();
    setCurrentRoom(room);
    
    if (room) {
      setRoomName(room.name || '');
      setRoomColor(room.color || '#ff0000');
      setUniqueRoomId(room.properties?.uniqueRoomId || '');
      setProximityDistance(room.properties?.proximityDistance || 5);
      setKind(room.kind || 'meeting');
      setIsDirty(false);
    }
  }, [plugin]);

  // Listen for room changes
  useEffect(() => {
    const handleRoomChanged = () => {
      const room = plugin.getCurrentRoom();
      setCurrentRoom(room);
      
      if (room) {
        setRoomName(room.name || '');
        setRoomColor(room.color || '#ff0000');
        setUniqueRoomId(room.properties?.uniqueRoomId || '');
        setProximityDistance(room.properties?.proximityDistance || 5);
        setKind(room.kind || 'meeting');
        setIsDirty(false);
      }
    };

    plugin.on('editModeDisabled', handleRoomChanged);
    plugin.on('roomDeleted', handleRoomChanged);
    plugin.on('roomPropertiesUpdated', handleRoomChanged);

    return () => {
      plugin.off('editModeDisabled', handleRoomChanged);
      plugin.off('roomDeleted', handleRoomChanged);
      plugin.off('roomPropertiesUpdated', handleRoomChanged);
    };
  }, [plugin]);

  const handlePropertyChange = (field: 'name' | 'color' | 'uniqueRoomId' | 'proximityDistance', value: string | number) => {
    if (field === 'name') {
      setRoomName(value as string);
    } else if (field === 'color') {
      setRoomColor(value as string);
    } else if (field === 'uniqueRoomId') {
      setUniqueRoomId(value as string);
    } else if (field === 'proximityDistance') {
      setProximityDistance(value as number);
    }
    setIsDirty(true);
  };

  const handleSave = async () => {
    if (!currentRoom || !isDirty) return;

    setIsSaving(true);
    try {
      // Update room properties
      const updatedRoom: VirtualRoom = {
        ...currentRoom,
        name: roomName.trim(),
        color: roomColor,
        properties: {
          uniqueRoomId: uniqueRoomId.trim(),
          proximityDistance
        },
        updatedAt: Date.now()
      };

      // Update the room properties in the plugin
      console.log('[VirtualRoomPropertiesPanel] Updating room properties via plugin:', {
        name: roomName.trim(),
        color: roomColor,
        properties: {
          uniqueRoomId: uniqueRoomId.trim(),
          proximityDistance
        }
      });
      
      plugin.updateRoomProperties({
        name: roomName.trim(),
        color: roomColor,
        properties: {
          uniqueRoomId: uniqueRoomId.trim(),
          proximityDistance
        },
        ...(kind !== 'reception' ? { kind } : {})
      });
      
      setIsDirty(false);
      console.log('[VirtualRoomPropertiesPanel] Room properties updated:', updatedRoom);
    } catch (error) {
      console.error('[VirtualRoomPropertiesPanel] Failed to update room properties:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (!currentRoom) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Select a room to edit its properties
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <KeyboardCaptureLayer />
      <Typography variant="h6" gutterBottom>
        Room Properties
      </Typography>
      
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Editing: {currentRoom.name}
      </Typography>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label="Room Name"
          value={roomName}
          onChange={(e) => handlePropertyChange('name', e.target.value)}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          fullWidth
          size="small"
          helperText="The display name for this room"
        />

        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Room Color
          </Typography>
          <ColorPicker
            value={roomColor}
            onChange={(newColor) => handlePropertyChange('color', newColor)}
            label=""
          />
        </Box>

        <TextField
          label="Unique Room ID"
          value={uniqueRoomId}
          onChange={(e) => handlePropertyChange('uniqueRoomId', e.target.value)}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          fullWidth
          size="small"
          helperText="A unique identifier for this room"
        />

        <TextField
          label="Proximity Distance"
          type="number"
          value={proximityDistance}
          onChange={(e) => handlePropertyChange('proximityDistance', Number(e.target.value))}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          fullWidth
          size="small"
          inputProps={{ min: 1, max: 100 }}
          helperText="Distance in tiles for proximity detection (1-100)"
        />

        <KindPicker
          value={kind}
          onChange={(next) => {
            setKind(next);
            setIsDirty(true);
          }}
        />

        {isDirty && (
          <Alert severity="info" sx={{ mt: 1 }}>
            Properties have been modified. Click Save to apply changes.
          </Alert>
        )}

        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!isDirty || isSaving || !roomName.trim() || !uniqueRoomId.trim()}
          size="small"
          sx={{ mt: 1 }}
        >
          {isSaving ? 'Saving...' : 'Save Properties'}
        </Button>

        <Divider sx={{ my: 1 }} />

        <Typography variant="subtitle2" gutterBottom>
          Spawn Point
        </Typography>
        <SpawnPointSection plugin={plugin} room={currentRoom} />
      </Box>
    </Box>
  );
};